import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '@/lib/api';
import { useLocale } from '@/lib/i18n';

interface InvitationPreview {
  email: string;
  name: string;
  role: string;
}

export default function InviteAcceptPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<InvitationPreview>(`/api/auth/invitations/${token}`)
      .then((invite) => {
        if (!cancelled) setPreview(invite);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < 12 || pending) return;
    setPending(true);
    setError(null);
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    try {
      await api(`/api/auth/invitations/${token}`, {
        method: 'POST',
        body: JSON.stringify({
          password,
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          name: name || undefined,
          phone: phone.trim() || null,
        }),
      });
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('prof.inviteExpired'));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md items-center px-4 py-10">
      <section className="w-full rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-sm">
        {unavailable ? (
          <div className="space-y-3">
            <h1 className="text-lg font-bold">{t('prof.inviteExpired')}</h1>
            <Link to="/login" className="inline-flex text-sm font-semibold text-amber-700">{t('prof.signIn')}</Link>
          </div>
        ) : preview === null ? (
          <p className="text-sm text-slate-500">{t('prof.accountHint')}</p>
        ) : (
          <form onSubmit={(event) => void submit(event)} className="space-y-3">
            <h1 className="text-lg font-bold">{t('prof.acceptInvite')}</h1>
            <p className="text-sm text-slate-500">{preview.email}</p>
            <label className="block text-xs font-semibold">
              {t('prof.firstName')}
              <input value={firstName} onChange={(event) => setFirstName(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs font-semibold">
              {t('prof.lastName')}
              <input value={lastName} onChange={(event) => setLastName(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs font-semibold">
              {t('prof.phone')}
              <input value={phone} onChange={(event) => setPhone(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs font-semibold">
              {t('prof.invitePassword')} *
              <span className="mt-1 flex gap-2">
                <input required minLength={12} type={visible ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                <button type="button" onClick={() => setVisible((current) => !current)} className="rounded-xl border border-slate-200 px-3 text-xs">
                  {visible ? t('prof.hidePassword') : t('prof.showPassword')}
                </button>
              </span>
            </label>
            {error && <p className="text-xs font-semibold text-rose-600">{error}</p>}
            <button type="submit" disabled={pending || password.length < 12} className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">
              {pending ? '…' : t('prof.acceptInvite')}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
